type ConversationEntry = {
  time?: string;
  speaker?: string;
  key: string;
  content: string;
};

export enum ParseTranscriptStatus {
  FormattedCompletely = 'formattedCompletely',
  InvalidFormat = 'invalidFormat',
}

export type ParseTranscriptResult =
  | { status: ParseTranscriptStatus.FormattedCompletely; conversation: ConversationEntry[]; id: string }
  | { status: ParseTranscriptStatus.InvalidFormat; fullConversation: string; id: string };

/**
 * Parses a raw transcript into an object with parsing status and entries.
 * @param text - Raw transcript text
 */
export function parseTranscript(recapId: string, text: string): ParseTranscriptResult {
  const lines = text.split(/\r?\n/);
  const conversation: ConversationEntry[] = [];
  let invalidCount = 0;

  for (let i = 0; i < lines.length; i++) {
    const raw = lines[i].trim();
    if (!raw) continue; // skip blank lines

    // Match timestamp with any-length hours (e.g., 200:59:59)
    const tsMatch = raw.match(/^(\d+:\d{2}:\d{2})\s+Speaker\s+(\d+):(?:\s*-\s*(.*))?$/);
    if (tsMatch) {
      const [, time, speakerId, inlineContent] = tsMatch;
      let content = inlineContent ?? '';

      // If no inline content, check next line
      if (!content) {
        const nextRaw = lines[i + 1]?.trim() ?? '';
        if (nextRaw.startsWith('-')) {
          content = nextRaw.replace(/^[-\s]+/, '');
          i++; // consume the content line
        }
      }

      if (content) {
        conversation.push({ key: `${recapId}_${speakerId}_${i}`, time, speaker: `Speaker ${speakerId}`, content });
      } else {
        invalidCount++;
      }
    } else if (raw.startsWith('-')) {
      // orphan content line
      invalidCount++;
    } else {
      // unexpected format
      invalidCount++;
    }
  }

  if (invalidCount === 0) {
    return { status: ParseTranscriptStatus.FormattedCompletely, conversation, id: recapId };
  }

  // On any parse errors, return raw text
  return {
    status: ParseTranscriptStatus.InvalidFormat,
    fullConversation: text.trim(),
    id: recapId,
  };
}
